import { startPackApiHooks } from './startPackApi';
import { iccidApiHooks } from './iccidApi';
import { numberDiscoveryApiHooks } from './numberDiscoveryApi';
import { emptySplitApi } from './injectApi';
import { advancedFeaturesApiHook } from './advancedFeaturesApi';

const stockApi = emptySplitApi.injectEndpoints({
  endpoints: (build) => ({
    getLocations: build.query({
      query: (query) => ({
        url: `/sales-location/location/query/${query}/`,
        method: 'GET',
      }),
      transformResponse: (response) => {
        const { data } = response;
        return data.map((item) => ({
          ...item,
          label: item.address,
          value: item.address,
        }));
      },
    }),
    getUserInfo: build.query({
      query: (data) => ({
        url: '/pos-manager/pos-manager/user/personal-info',
        method: 'POST',
        data: {
          idCardNumber: data.idCardNumber.toUpperCase(),
          idCardPin: data.idCardPin.toUpperCase(),
        },
      }),
      transformResponse: (response) => response.data,
    }),
    getOrganizations: build.query({
      query: () => ({
        url: `/sales-admin/sales/admin/pos-manager/organizations`,
        method: 'GET',
      }),
      transformResponse: (response) =>
        response.map((item) => ({
          label: item.title,
          ...item,
        })),
    }),
    getOrganizationsTree: build.query({
      query: (id) => ({
        url: `/sales-admin/sales/admin/pos-manager/organizations/${id}/tree`,
        method: 'GET',
      }),
    }),
    getPosManagerOrganizations: build.query({
      query: (hubId) => ({
        url: `/sales-admin/sales/admin/pos-manager/organizations/hubs/${hubId}`,
        method: 'GET',
      }),
      transformResponse: (response) =>
        response.map((item) => ({
          label: item.title,
          ...item,
        })),
    }),
    getHubList: build.query({
      query: () => ({
        url: `/sales-admin/sales/admin/hubs`,
        method: 'GET',
      }),
      transformResponse: (response) =>
        response.map((item) => ({
          ...item,
          label: item.title ?? item.hubTitle,
          title: item.title ?? item.hubTitle,
          id: item.hubId,
        })),
      providesTags: ['Stock'],
    }),
    getTransactionHistory: build.query({
      query: (params) => ({
        url: `/sales-history/sales/transaction/history`,
        method: 'GET',
        params: {
          size: 10,
          ...params,
        },
      }),
      providesTags: ['History'],
    }),
    getTransferOwnershipHistory: build.query({
      query: (params) => ({
        url: `/sales-history/sales/transferownership/history`,
        method: 'GET',
        params,
      }),
      providesTags: ['TransferOwnership'],
    }),
    getTransferOwnershipFiles: build.query({
      query: (params) => ({
        url: `/sales-history/sales/transferownership/downloads/files`,
        method: 'GET',
        params,
        headers: {
          'Content-Type': 'application/octet-stream',
        },
        responseType: 'blob',
      }),
      providesTags: ['TransferOwnership'],
    }),
    createDealer: build.mutation({
      query: (body) => ({
        url: 'sales-admin/sales/admin/pos-manager/organizations/dealer',
        method: 'POST',
        data: body,
      }),
    }),

    changeTransferOwnershipStatus: build.mutation({
      query: (body) => ({
        url: `/sales-history/sales/transferownership/log`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['TransferOwnership'],
    }),
    getDownloadContract: build.query({
      query: (params) => ({
        url: '/contract-manager/contract/download',
        method: 'GET',
        params,
        headers: {
          'Content-Type': 'application/octet-stream',
        },
        responseType: 'blob',
      }),
      providesTags: ['History'],
    }),
    getStocksByPartner: build.query({
      query: ({ partnerId, ...params }) => ({
        url: `/sales-admin/sales/admin/stock/partners/${partnerId}`,
        method: 'GET',
        params,
      }),
    }),
    getStocksByPartnerDownload: build.query({
      query: ({ partnerId, ...params }) => ({
        url: `/sales-admin/sales/admin/stock/partners/${partnerId}/download`,
        method: 'GET',
        params,
      }),
    }),
    getStocksByHub: build.query({
      query: ({ hubId, ...params }) => ({
        url: `/sales-admin/sales/admin/stock/hubs/${hubId}`,
        method: 'GET',
        params,
      }),
    }),
    getStocksByHubDownload: build.query({
      query: ({ hubId, ...params }) => ({
        url: `/sales-admin/sales/admin/stock/hubs/${hubId}/download`,
        method: 'GET',
        params,
      }),
    }),

    getDealerArchive: build.query({
      query: (params) => ({
        url: `/sales-history/sales/dealer/transactions/${params.providerId}`,
        method: 'GET',
        params,
      }),
    }),
    getDealerArchiveDeatailed: build.query({
      query: (params) => ({
        url: 'sales-history/sales/dealer/support/info',
        method: 'GET',
        params,
      }),
    }),
    getDevices: build.query({
      query: () => ({
        url: `/pos-manager/pos/admin/organization/identifier`,
        method: 'GET',
      }),
    }),
    postNotification: build.mutation({
      query: (data) => ({
        url: `/pos-manager/pos/admin/notifications/send`,
        method: 'POST',
        data: data.form,
        headers: {
          'Content-Type': 'multipart/form-data',
          'Notification-Key': data.organizationsType,
        },
      }),
    }),
  }),
});
export const api = {
  useGetLocations: stockApi.useGetLocationsQuery,
  useCreateDealer: stockApi.useCreateDealerMutation,
  useGetUserInfo: stockApi.useLazyGetUserInfoQuery,
  useGetOrganizations: stockApi.useGetOrganizationsQuery,
  useGetOrganizationsTree: stockApi.useGetOrganizationsTreeQuery,
  useGetPosManagerOrganizations: stockApi.useGetPosManagerOrganizationsQuery,
  useGetHubList: stockApi.useGetHubListQuery,
  useGetTransactionHistory: stockApi.useGetTransactionHistoryQuery,
  useGetDownloadContract: stockApi.useLazyGetDownloadContractQuery,
  useGetStocksByPartner: stockApi.useGetStocksByPartnerQuery,
  useGetStocksByHub: stockApi.useGetStocksByHubQuery,
  useGetStocksByPartnerDownload: stockApi.useGetStocksByPartnerDownloadQuery,
  useGetStocksByHubDownloadQuery: stockApi.useGetStocksByHubDownloadQuery,
  useGetDealerArchive: stockApi.useGetDealerArchiveQuery,
  useGetDealerArchiveDetailed: stockApi.useLazyGetDealerArchiveDeatailedQuery,
  useLazyGetDealerArchive: stockApi.useLazyGetDealerArchiveQuery,
  useGetTransferOwnershipHistory: stockApi.useGetTransferOwnershipHistoryQuery,
  useGetTransferOwnershipFiles: stockApi.useLazyGetTransferOwnershipFilesQuery,
  useChangeTransferOwnershipStatus: stockApi.useChangeTransferOwnershipStatusMutation,
  useGetDevices: stockApi.useGetDevicesQuery,
  usePostNotification: stockApi.usePostNotificationMutation,
  ...startPackApiHooks,
  ...iccidApiHooks,
  ...numberDiscoveryApiHooks,
  ...advancedFeaturesApiHook,
};

export default stockApi;
